import { z } from 'zod';
import type { AppModel } from '@/models/AppModel';
import type { LogicNodeDefinition } from '@/models/logic/LogicNodeDefinition';
import type {
  BuildAndConnectNodesFn,
  CreateContextMenuItemsData,
} from '@/models/tiles/TileDefinition';
import {
  getGlobalDirection,
  getLocalSide,
  getNeighborPosition,
} from '@/models/logic/utils';
import { Point } from '@/models/Point';
import { Rotation } from '@/models/data/Rotation';
import { opposite } from '@/utils/direction';
import { Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import { configureTile } from './configuration';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from './activeNodes';

export const clockedConfig = { clocked: z.boolean().default(false) };
export const clockedRuntime = { sampled: z.boolean().default(false) };
export const clockIo = { type: TileSideIOType.INPUT, forceConnection: true };
export function clockModeItem({
  instance,
  chipEditorModel,
}: CreateContextMenuItemsData<{ clocked: boolean }>) {
  return {
    label: instance.state.clocked
      ? 'Use live updates'
      : 'Sample on rising clock (bottom)',
    action: () =>
      configureTile(
        instance,
        chipEditorModel,
        { clocked: !instance.state.clocked },
        'Change clock mode',
      ),
  };
}
export function clockNode(type: string): LogicNodeDefinition {
  return {
    type: `${type}-clock`,
    stateSchema: z.null(),
    createDefaultState: () => null,
    process: (input) => input,
  };
}
/** Independent bottom clock and left data pins; neither signal is ORed into the other. */
export function clockedBuild(
  type: string,
  hasData: boolean,
  optionalClock = false,
): BuildAndConnectNodesFn<any> {
  return ({
    graph,
    instance,
    tileManager,
    sourceNodeId,
    sourceTileId,
    entryDirection,
  }) => {
    if (!instance.logicNodeIds?.right) {
      const out = graph.addNode(type);
      const clock =
        !optionalClock || instance.state.clocked
          ? graph.addNode(`${type}-clock`)
          : undefined;
      Object.assign(out.state, instance.state);
      instance.logicNodeIds = {
        top: out.id,
        right: out.id,
        left: out.id,
        bottom: clock?.id,
      };
    }
    if (sourceNodeId && entryDirection !== undefined) {
      const side = getLocalSide(
        opposite(entryDirection),
        instance.meta.rotation as Rotation,
      );
      if (
        (side === Side.BOTTOM && instance.logicNodeIds.bottom) ||
        (hasData && side === Side.LEFT)
      )
        graph.addEdge(sourceNodeId, instance.logicNodeIds[side]!, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: side,
        });
    }
    const direction = getGlobalDirection(Side.RIGHT, instance.meta.rotation as Rotation)!;
    const neighbor = tileManager.get(
      getNeighborPosition(Point.fromData(instance.location), direction),
    );
    return neighbor
      ? [
          {
            tile: neighbor,
            sourceNodeId: instance.logicNodeIds.right!,
            sourceTileId: instance.id,
            entryDirection: direction,
          },
        ]
      : [];
  };
}
// GraphManager assigns ['tile', tileId, side], prefixed with each nested chip's path.
// Removing only the terminal side keeps separate placed instances independent.
const tilePath = (target: ActiveExtensionNode) =>
  target.node.runtimeKey?.replace(/,"(?:top|right|bottom|left)"\]$/, ']');
/** Index pairs on graph publication, then inspect only clocks in the processed set. */
export function watchClocks(
  app: AppModel,
  type: string,
  sample: (target: ActiveExtensionNode) => void,
) {
  let dispose: (() => void)[] = [];
  return watchActiveExtensionNodes(app, [type, `${type}-clock`], (targets) => {
    dispose.forEach((fn) => fn());
    dispose = [];
    const groups = new Map<ActiveExtensionNode['simulation'], ActiveExtensionNode[]>();
    for (const target of targets) {
      let group = groups.get(target.simulation);
      if (!group) groups.set(target.simulation, (group = []));
      group.push(target);
    }
    for (const [simulation, group] of groups) {
      const outputs = new Map(
        group.filter((t) => t.node.type === type).map((t) => [tilePath(t), t]),
      );
      const clocks = new Map(
        group
          .filter((t) => t.node.type !== type)
          .map((t) => [
            t.node.id,
            {
              target: t,
              previous: t.graph.getNodeInput(t.node.id),
              output: outputs.get(tilePath(t)),
            },
          ]),
      );
      dispose.push(
        simulation.subscribeProcessed((ids) => {
          for (const id of ids.size < clocks.size ? ids : clocks.keys()) {
            if (!ids.has(id)) continue;
            const entry = clocks.get(id);
            if (!entry) continue;
            const high = entry.target.node.output;
            const rising = high && !entry.previous;
            entry.previous = high;
            if (rising && entry.output) sample(entry.output);
          }
        }),
      );
    }
  });
}
export function watchSampledInput(
  app: AppModel,
  type: string,
  value: (target: ActiveExtensionNode) => boolean,
) {
  return watchClocks(app, type, (target) => {
    const state = target.node.state;
    if (!state.enabled || !state.clocked) return;
    const next = value(target);
    if (state.sampled === next) return;
    state.sampled = next;
    target.simulation.triggerUpdate(target.node.id);
  });
}
